import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton from "../../buttons/button";
import InputField from "../../forms/input-field";
import TabsComponent from "../../helper-components/tabs-component";

import { useDispatch } from "react-redux";
import { StoreFirmCreateAction, StorePartnerCreateAction } from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import StoreFirmShopAddressTab from "./firm-shop-address";
import SelectField from "../../forms/select-field";
import CheckBox from "../../forms/check-box-field";
import StorePanGstTab from "./pan-gst-tab";
import StorePartnerDetailsTab from "./partner-details-tab";
import { GST_APPLIED, GST_NOT_APPLICABLE } from "./gst-applicable";

// GST columns as /store/getById returns them: `gst_applicable` holds one of
// the gst-applicable labels (or null), and `gst_documents` is a list of URLs.

const readGstApplicable = (record) => {
  const raw = record?.gst_applicable ?? record?.is_gst_applicable ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  if (raw === GST_APPLIED || raw === GST_NOT_APPLICABLE) return raw;
  // Older records saved a yes/no flag.
  const yes =
    raw === true ||
    ["true", "yes", "1", "y", "gst applied", "applicable"].includes(
      String(raw).trim().toLowerCase()
    );
  return yes ? GST_APPLIED : GST_NOT_APPLICABLE;
};

// The form edits one document; the API stores a list.
const readGstDocument = (record) => {
  const docs = record?.gst_documents;
  if (Array.isArray(docs)) return docs[0] ?? "";
  if (typeof docs === "string" && docs) return docs;
  return record?.gst_document ?? "";
};

const StoreFirmTab = ({
  id,
  setStoreData,
  storeData,
  initialValue,
  disabled = false,
  // Reports a finished step to the tab container, which unlocks the next tab.
  onProgress = () => {},
}) => {
  const [currentTab, setCurrentTab] = useState("Address of Firm Correspondence");
  const [firmTypeOptions, setFirmTypeOptions] = useState([]);
  const [fetchedPartners, setFetchedPartners] = useState([]);
  // Brand name the store must trade under, from the Account Details module.
  const [accountFirmName, setAccountFirmName] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    api
      .get("/fofo/storeAccountDetails/getAll")
      .then((res) => {
        const data = res?.data?.data;
        const record = Array.isArray(data) ? data[0] : data;
        setAccountFirmName(record?.firm_name ?? "");
      })
      .catch((err) => {
        console.error(
          "Failed to fetch account details:",
          err?.response?.data ?? err?.message
        );
      });
  }, []);

  // Falls back to first person while the name or brand is not yet known.
  const authorisedPersonName = storeData?.personal?.authorised_person_name;
  const declarant = authorisedPersonName
    ? `${authorisedPersonName} agrees to apply and run their store`
    : "I agree to apply and run my store";
  const terms = [
    {
      label: accountFirmName
        ? `${declarant} with “${accountFirmName}”.`
        : `${declarant}.`,
      name: "firm_agreement_with_davaindia_conditions",
      isRequired: false,
    },
  ];

  useEffect(() => {
    if (!id) return;
    api
      .get(`/fofo/partnerDetails/getByStoreId?store_id=${id}`)
      .then((res) => {
        const list = res.data?.data ?? [];
        setFetchedPartners(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(
          "Failed to fetch partner details:",
          err?.response?.data ?? err?.message
        );
      });
  }, [id]);

  useEffect(() => {
    api
      .get("/fofo/firmType/getAll")
      .then((res) => {
        const list = res.data?.data ?? [];
        console.log("Firm types fetched:", list);
        const options = list.map((t) => ({ label: t.firm_type, value: t.firm_type }));
        setFirmTypeOptions(options);
      })
      .catch((err) => {
        console.error("Failed to fetch firm types:", err?.response?.data ?? err?.message);
      });
  }, []);
  // Prefer the partner list endpoint, else whatever getById already nested.
  const partnerRecords = fetchedPartners.length
    ? fetchedPartners
    : initialValue?.partners ??
      initialValue?.partner ??
      initialValue?.partnerDetails ??
      initialValue?.partner_details ??
      [];

  // API returns unprefixed columns (relation_with); form fields are partner_*.
  const savedPartners = (Array.isArray(partnerRecords) ? partnerRecords : []).map(
    (p) => ({
      ...p,
      partner_name: p.partner_name ?? p.name ?? "",
      partner_relation_type: p.partner_relation_type ?? "",
      partner_relation_name: p.partner_relation_name ?? "",
      partner_relation_with: p.partner_relation_with ?? p.relation_with ?? "",
      partner_age: p.partner_age ?? p.age ?? "",
      partner_pincode: p.partner_pincode ?? p.pincode ?? "",
      partner_area: p.partner_area ?? p.area ?? "",
      partner_city: p.partner_city ?? p.city ?? "",
      partner_state: p.partner_state ?? p.state ?? "",
      partner_full_address: p.partner_full_address ?? p.full_address ?? "",
      partner_landmark: p.partner_landmark ?? p.landmark ?? "",
      partner_town_or_village:
        p.partner_town_or_village ?? p.town_village ?? "",
      partner_aadhar_number: p.partner_aadhar_number ?? p.aadhaar_no ?? "",
      partner_aadhar_front:
        p.partner_aadhar_front ?? p.aadhaar_front_image ?? "",
      partner_aadhar_back: p.partner_aadhar_back ?? p.aadhaar_back_image ?? "",
    })
  );

  const initialValues = {
    store_id: id ?? null,
    firm_name: initialValue?.firm_name ?? "",
    firm_type: initialValue?.firm_type ?? "",
    firm_authorised_person: initialValue?.firm_authorised_person ?? "",
    designation: initialValue?.designation ?? "",
    client_firm_name: initialValue?.client_firm_name ?? "",
    can_change_firm_name: initialValue?.can_change_firm_name ?? false,
    firm_address: initialValue?.firm_address ?? "",
    firm_area: initialValue?.firm_area ?? "",
    firm_landmark: initialValue?.firm_landmark ?? "",
    firm_town_or_village: initialValue?.firm_town_or_village ?? "",
    firm_city: initialValue?.firm_city ?? "",
    firm_state: initialValue?.firm_state ?? "",
    firm_pin_code: initialValue?.firm_pin_code ?? "",
    firm_country_region: initialValue?.firm_country_region ?? "india",
    firm_region: initialValue?.firm_region ?? "",
    firm_agreement_with_davaindia_conditions:
      initialValue?.firm_agreement_with_davaindia_conditions ?? false,
    pan_type: initialValue?.pan_type ?? "",
    pan_number: initialValue?.pan_number ?? "",
    pan_pic_front: initialValue?.pan_pic_front ?? "",
    pan_pic_back: initialValue?.pan_pic_back ?? "",
    gst_number: initialValue?.gst_number ?? "",
    gst_applicable: readGstApplicable(initialValue),
    gst_document: readGstDocument(initialValue),
    partners: savedPartners.length
      ? savedPartners
      : [{
          partner_name: "",
          partner_relation_with: "",
          partner_relation_type: "",
          partner_relation_name: "",
          partner_age: "",
          partner_pincode: "",
          partner_area: "",
          partner_city: "",
          partner_state: "",
          partner_full_address: "",
          partner_landmark: "",
          partner_town_or_village: "",
          partner_aadhar_number: "",
          partner_aadhar_front: "",
          partner_aadhar_back: "",
        }],
  };

  const validationSchema = Yup.object().shape({
    pan_number: Yup.string()
      .required("PAN number is required")
      .matches(/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/, "Invalid PAN number"),
    firm_address: Yup.string().required("Firm address is required"),
    firm_area: Yup.string().required("Firm area is required"),
    firm_landmark: Yup.string().required("Firm landmark is required"),
    firm_town_or_village: Yup.string().required(
      "Firm town or village is required"
    ),
    firm_city: Yup.string().required("Firm city is required"),
    firm_state: Yup.string().required("Firm state is required"),
    firm_pin_code: Yup.string()
      .required("Firm pin code is required")
      .matches(/^[1-9][0-9]{5}$/, "Invalid Pin Code"),
    firm_country_region: Yup.string().required(
      "Firm country or region is required"
    ),
    pan_pic_front: Yup.string().required("PAN Front image is required"),
    pan_pic_back: Yup.string().required("PAN Back image is required"),
  });

  const onSubmit = (values, actions) => {
    const { partners, firm_region, gst_document, ...firmFields } = values;
    const gstApplied = firmFields.gst_applicable === GST_APPLIED;
    const firmPayload = {
      ...firmFields,
      gst_number: gstApplied ? firmFields.gst_number : "",
      gst_documents: gstApplied && gst_document ? [gst_document] : [],
    };

    dispatch(StoreFirmCreateAction(firmPayload, onSuccess, onError));

    partners.forEach((p) => {
      const partnerPayload = {
        store_id: values.store_id,
        partner_name: p.partner_name,
        relation_with: p.partner_relation_with,
        partner_relation_type: p.partner_relation_type,
        partner_relation_name: p.partner_relation_name,
        age: p.partner_age,
        pincode: p.partner_pincode,
        area: p.partner_area,
        city: p.partner_city,
        state: p.partner_state,
        full_address: p.partner_full_address,
        landmark: p.partner_landmark,
        town_village: p.partner_town_or_village,
        aadhaar_no: p.partner_aadhar_number,
        aadhaar_front_image: p.partner_aadhar_front,
        aadhaar_back_image: p.partner_aadhar_back,
      };
      dispatch(StorePartnerCreateAction(partnerPayload, () => {}, onError));
    });
  };

  const onSuccess = (data) => {
    onProgress({ is_firm_details_tab_completed: true });
    setStoreData({
      ...storeData,
      firm: data?.data,
    });
    successToast(data?.data?.message);
  };
  const onError = (data) => {
    errorToast(data?.data?.message ?? data?.message);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      // validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors }) => {
        
        
        return (
          <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
            <div className="flex flex-col justify-between h-full ">
              <div>
                <div className="flex flex-wrap items-center">
                  <div className="w-full 2xl:w-[35%] lg:w-[50%] px-5 xl:px-10">
                    <InputField
                      isDisabled={disabled}
                      type="text"
                      name="firm_name"
                      placeholder="Enter Firm Name"
                      label="Firm Name"
                      labelWidth="10.5rem"
                    />
                    <SelectField
                      isDisabled={disabled}
                      placeholder={"Select Firm Type"}
                      options={firmTypeOptions}
                      label={"Firm Type"}
                      id={"firm_type"}
                      name="firm_type"
                      isSearchable={true}
                      errMsgContainerClass="ps-2"
                      labelWidth="10.5rem"
                    />
                    <InputField
                      isDisabled={disabled}
                      type="text"
                      name="firm_authorised_person"
                      placeholder="Enter Firm Authorised Person"
                      label="Firm Authorised Person"
                      labelWidth="10.5rem"
                    />

                    {/* Designation is hidden but kept in the form values, so a
                        save does not blank it and the LOI still receives it. */}
                    <InputField
                      isDisabled={disabled}
                      type="text"
                      name="client_firm_name"
                      placeholder="Enter Client Firm Name"
                      label="Client Firm Name"
                      labelWidth="10.5rem"
                    />
                  </div>
                  <div className="w-[80%] 2xl:w-[20%] lg:w-[30%] px-5 xl:px-10 mt-10 lg:mt-0">
                    <div className="border-D0D5DD border border-1 py-2 px-2 rounded-lg">
                      <CheckBox items={items} />
                    </div>
                  </div>
                </div>
                <TabsComponent
                  handleClickfn={(item) => {
                    setCurrentTab(item);
                  }}
                  itemTabs={itemTabs}
                  setCurrentTab={setCurrentTab}
                  currentTab={currentTab}
                />
                {currentTab === "Address of Firm Correspondence" && (
                  <StoreFirmShopAddressTab disabled={disabled} />
                )}
                {currentTab === "Pan & GST" && (
                  <StorePanGstTab disabled={disabled} />
                )}
                {/* Read-only: partner data is maintained elsewhere. */}
                {currentTab === "Partner Details" && (
                  <StorePartnerDetailsTab disabled={true} />
                )}
              </div>
              <div
                className="sticky"
                style={{
                  bottom: "0",
                }}
              >
                <div
                  style={{
                    height: "5rem",
                  }}
                  className="px-5 xl:px-10 bg-D0D5DD flex items-center"
                >
                  <CheckBox items={terms} />
                </div>

                {!disabled && (
                  <div className="flex justify-end px-10 mb-5x  bg-white py-4 invoice-bottom-container">
                    <AddButton
                      onclkFn={() => {
                        if (Object.keys(errors).length !== 0) {
                          const currentIndex = itemTabs.indexOf(currentTab);
                          let nextTab;

                          if (currentIndex !== -1) {
                            // Go to the next tab based on the current tab index
                            nextTab =
                              currentIndex === itemTabs.length - 1
                                ? itemTabs[0] // Loop back to the first tab if the last tab is reached
                                : itemTabs[currentIndex + 1];

                            setCurrentTab(nextTab);
                          }
                        }
                      }}
                      prefixIcon={false}
                      text="Save"
                      type="submit"
                    />
                  </div>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StoreFirmTab;
const itemTabs = ["Address of Firm Correspondence", "Pan & GST", "Partner Details"];
const items = [
  {
    label: "Can change firm name",
    name: "can_change_firm_name",
    isRequired: false,
  },
];
